/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';

import { Button, ContextualHelp, InlineNotification, Link, TemporaryDrawer, Typography2 } from '@coursera/cds-core';

import PlagiarismStatusDropDown from 'bundles/academic-integrity/components/PlagiarismStatusDropDown';
import { useGetVivaExamLatestResultSummary } from 'bundles/academic-integrity/components/viva-exam/api/VivaExamLatestResultSummaryDataProvider';
import { useGetVivaExamSubmissionData } from 'bundles/academic-integrity/components/viva-exam/api/VivaExamLatestSubmissionDataProvider';
import IntegrityCheckAnalysis from 'bundles/academic-integrity/components/viva-exam/grader-view/IntegrityCheckAnalysis';
import IntegrityCheckPanel from 'bundles/academic-integrity/components/viva-exam/grader-view/IntegrityCheckPanel';
import {
  AssignmentType,
  FlagStatus,
} from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';
import { PlagiarismContext } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import PlagiarismFlagCommentInput from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismFlagCommentInput';
import PlagiarismFlagComments from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismFlagComments';
import PlagiarismFlagMemberSelect from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismFlagMemberSelect';
import { COMMENT_MAX_LEN } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/constants/PlagiarismFlagConstants';
import type Team from 'bundles/author-assignment-grading/components/common/types/Team';
import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import type GroupMember from 'bundles/authoring/groups/models/GroupMember';
import { buildPartnerHelpUrl } from 'bundles/common/utils/urlUtils';
import type {
  CheatingCaseRuling,
  CheatingCaseStaffComment,
} from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';
import { CHEATING_CASE_RULINGS } from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';

import _t from 'i18n!nls/academic-integrity';

const { HeadingGroup, Content, Actions } = TemporaryDrawer;

const styles = {
  actions: (vivaExamEnabled: boolean) => css`
    padding-top: 0;
    box-shadow: ${vivaExamEnabled ? ' 0 -3px 6px 0 rgb(0 0 0 / 5%)' : 'none'};
  `,
  actionButtons: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  errorNotification: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

type Props = {
  team?: Team;
  groupId?: string;
  isAnonymized?: boolean | null;
  learners?: Array<GroupMember>;
  isReadOnly: boolean;
  isOpen: boolean;
  ruling?: CheatingCaseRuling;
  guiltyMembers?: Array<any>;
  pendingComment: string;
  saving: boolean;
  cheatingCaseStaffComments: Array<CheatingCaseStaffComment>;
  cheatingCaseCommentsByCase: { [caseId: string]: Array<CheatingCaseStaffComment> };
  onSlideOutSave: () => any;
  onSlideOutClose: () => void;
  onSelectStatus: (status: CheatingCaseRuling) => void;
  onUpdateComment: (pendingComment: string) => void;
  onUpdateGuiltyMembers: (guiltyMembers: Array<number>) => void;
  assignmentId: string;
  submitterId: string;
  submitterName?: string;
};

/**
 * The SlideOut modal that allows for viewing and updating a plagiarism flag & integrity check scores
 */

/** */
const IntegrityFlagsPanelSlideOut = ({
  team,
  groupId,
  isAnonymized,
  learners,
  isReadOnly,
  isOpen,
  ruling,
  guiltyMembers,
  pendingComment,
  saving,
  cheatingCaseStaffComments,
  cheatingCaseCommentsByCase,
  onSlideOutSave,
  onSlideOutClose,
  onSelectStatus,
  onUpdateComment,
  onUpdateGuiltyMembers,
  assignmentId,
  submitterId,
  submitterName,
}: Props) => {
  const submitterIdNumber = Number(submitterId) || 0;

  const { setActiveIntegrityFlagCount, vivaExamEnabled } = useContext(PlagiarismContext);

  const [showIntegrityCheckAnalysis, setShowIntegrityCheckAnalysis] = useState(false);
  const { vivaExamQuestions, errorFromLatestSubmissionData } = useGetVivaExamSubmissionData(
    assignmentId,
    submitterIdNumber,
    AssignmentType.SGA
  );
  const { vivaExamResultSummary, errorFromLatestResultSummary, refetchVivaExamResultSummary } =
    useGetVivaExamLatestResultSummary(assignmentId, submitterIdNumber);
  const vivaExamResultConclusion = vivaExamResultSummary?.conclusion;
  const vivaExamFlagRaised = vivaExamResultSummary?.flagStatus === FlagStatus.INTEGRITY_ISSUE_FOUND;

  // Check if the error is a NOT_FOUND error, if so we don't want to display the error since this
  // occurs when the learner did not start the viva exam but viva is enabled which is not an error
  const hasNotFoundError = errorFromLatestResultSummary?.graphQLErrors?.some(
    (error) => error?.extensions?.errorType === 'NOT_FOUND'
  );

  const displayError = errorFromLatestResultSummary && !hasNotFoundError;

  const handleFeedbackSubmitted = () => {
    refetchVivaExamResultSummary();
  };

  useEffect(() => {
    // if submitterId changes, reset the active integrity flag count
    setActiveIntegrityFlagCount(0);
  }, [submitterId, setActiveIntegrityFlagCount]);

  useEffect(() => {
    // adding the active integrity flag count 1 when the viva exam flag is raised
    if (vivaExamFlagRaised) {
      setActiveIntegrityFlagCount((prevCount) => {
        return prevCount < 2 ? prevCount + 1 : prevCount;
      });
    }
  }, [vivaExamFlagRaised, setActiveIntegrityFlagCount, submitterId]);

  const handleViewAnalysisClick = () => {
    setShowIntegrityCheckAnalysis(true);
  };

  const handleBackClick = () => {
    setShowIntegrityCheckAnalysis(false);
  };

  const invalidGuiltyMembers =
    team &&
    (ruling === CHEATING_CASE_RULINGS.UNRESOLVED || ruling === CHEATING_CASE_RULINGS.RESOLVED_CHEATING) &&
    guiltyMembers &&
    guiltyMembers.length === 0;

  const noComment = pendingComment.length === 0;
  const invalidComment = pendingComment.length > COMMENT_MAX_LEN;
  const enableSave = !noComment && !invalidComment && !invalidGuiltyMembers;

  const description = isReadOnly
    ? _t(
        'You can view the plagiarism flag status and comments for this assignment. For peer reviews, learners can add comments.'
      )
    : _t(
        'To update the plagiarism flag status, you must also leave a comment. Comments are for internal staff only. Comments may be added without a status update.'
      );

  const plagiarismFlagCommentInput = !isReadOnly && (
    <PlagiarismFlagCommentInput
      onUpdateComment={onUpdateComment}
      pendingComment={pendingComment}
      vivaExamEnabled={vivaExamEnabled}
    />
  );

  if (showIntegrityCheckAnalysis && vivaExamResultSummary && vivaExamEnabled) {
    return (
      <IntegrityCheckAnalysis
        onBackButtonClick={handleBackClick}
        open={showIntegrityCheckAnalysis}
        vivaExamQuestions={vivaExamQuestions}
        vivaExamResultConclusion={vivaExamResultConclusion}
        submitterName={submitterName}
        hasErrorFromLatestSubmissionData={Boolean(errorFromLatestSubmissionData)}
      />
    );
  }
  return (
    <TemporaryDrawer
      className="rc-PlagiarismFlagSlideOut"
      open={isOpen}
      onClose={onSlideOutClose}
      width="medium"
      anchor="right"
      sticky="actions"
    >
      <HeadingGroup>{vivaExamEnabled ? _t('Integrity Flags') : _t('Plagiarism Flag')}</HeadingGroup>
      <Content>
        {/** Displaying the new section in the Integrity Flags Drawer to add Integrity Check section */}
        {submitterIdNumber && vivaExamEnabled ? (
          <Fragment>
            {displayError && (
              <InlineNotification severity="error" css={styles.errorNotification}>
                {_t('There was an issue with retrieving the learner’s integrity check data. Please try again later.')}
              </InlineNotification>
            )}
            <IntegrityCheckPanel
              onViewAnalysisClick={handleViewAnalysisClick}
              vivaExamResultSummary={vivaExamResultSummary || undefined}
              onFeedbackSubmitted={handleFeedbackSubmitted}
              hasErrorFromLatestResultSummary={Boolean(errorFromLatestResultSummary)}
            />
            <ContextualHelp
              helpToggleProps={{
                'aria-label': _t('Information about the integrity check'),
              }}
              tooltipProps={{
                closeButtonProps: {
                  'aria-label': _t('Close information about the integrity check'),
                },
                cta: (
                  <Typography2 variant="bodySecondary" component="span">
                    <Link
                      variant="quiet"
                      href={buildPartnerHelpUrl('360024326892')}
                      target="_blank"
                      aria-label={_t('Learn more about flagging submissions')}
                    >
                      <strong>{_t('Learn More')}</strong>
                    </Link>
                  </Typography2>
                ),
              }}
              label={
                <Typography2 variant="subtitleLarge" component="span">
                  {_t('Plagiarism')}
                </Typography2>
              }
            >
              <Typography2 variant="bodyPrimary" component="span">
                {_t(
                  'Course staff can flag submissions they suspect may have been plagiarized to track flagged submissions and take appropriate action following your institution’s policies.'
                )}
              </Typography2>
            </ContextualHelp>
          </Fragment>
        ) : (
          <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
            {`${description} `}
            <PartnerHelpLink articleId="360024326892" />.
          </Typography2>
        )}
        <PlagiarismStatusDropDown
          ruling={ruling}
          disabled={cheatingCaseStaffComments.length === 0}
          onSelect={onSelectStatus}
          readOnly={isReadOnly}
          vivaExamEnabled={vivaExamEnabled}
        />
        {team && (
          <PlagiarismFlagMemberSelect
            onUpdateGuiltyMembers={onUpdateGuiltyMembers}
            team={team}
            guiltyMembers={guiltyMembers || []}
            groupId={groupId}
            isAnonymized={isAnonymized}
          />
        )}
        <PlagiarismFlagComments
          cheatingCaseComments={cheatingCaseCommentsByCase}
          learners={learners}
          isAnonymized={isAnonymized}
        />
        {vivaExamEnabled && plagiarismFlagCommentInput}
      </Content>
      <Actions css={styles.actions(vivaExamEnabled)}>
        {!vivaExamEnabled && plagiarismFlagCommentInput}
        <div css={styles.actionButtons}>
          {!isReadOnly && (
            <Button
              size="medium"
              variant="primary"
              onClick={onSlideOutSave}
              disabled={!enableSave}
              data-test="primary-button"
            >
              {saving ? _t('Saving...') : _t('Save')}
            </Button>
          )}
          <Button size="medium" variant="secondary" onClick={onSlideOutClose} data-test="secondary-button">
            {isReadOnly ? _t('Close') : _t('Cancel')}
          </Button>
        </div>
      </Actions>
    </TemporaryDrawer>
  );
};

export default IntegrityFlagsPanelSlideOut;
